import { Link } from "@tanstack/react-router"
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconHome2,
  IconRefresh,
} from "@tabler/icons-react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"

/**
 * Router-level fallback components, wired as defaults on the router in
 * `main.tsx` so every route inherits sensible loading / error / 404 UI
 * without declaring them per-file. Individual routes can still override
 * via `pendingComponent` / `errorComponent` / `notFoundComponent`.
 */

/**
 * Shown while a `loader` / `beforeLoad` is in flight: a quiet page-shaped
 * skeleton rather than a spinner, so the layout doesn't jump on arrival.
 */
export function RoutePending() {
  return (
    <div
      aria-busy="true"
      aria-label="Loading"
      className="flex min-h-dvh flex-col gap-6 bg-ground px-4 pt-8 md:px-8 xl:px-10"
    >
      <div className="flex flex-col gap-2.5">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-7 w-64" />
        <Skeleton className="h-3.5 w-96 max-w-full" />
      </div>
      <Skeleton className="h-28 rounded-xl" />
      <div className="grid gap-4 lg:grid-cols-3">
        <Skeleton className="h-64 rounded-xl lg:col-span-2" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </div>
  )
}

/** Thrown when a loader rejects or a route component throws on render. */
export function RouteError({
  error,
  reset,
}: {
  error: Error
  reset: () => void
}) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-ground px-6">
      <div className="panel flex w-full max-w-md flex-col items-start gap-5 p-6">
        <span className="flex size-10 items-center justify-center rounded-lg bg-fault-soft text-fault shadow-[inset_0_0_0_1px_var(--fault-ring)]">
          <IconAlertTriangle size={20} stroke={1.7} />
        </span>
        <div className="flex flex-col gap-1.5">
          <h1 className="text-heading">This page didn&apos;t load</h1>
          <p className="text-sm text-pretty text-ink-muted">
            Something went wrong on our side while opening it. Nothing you
            did was lost — try again, or head back to the dashboard.
          </p>
          {error?.message && (
            <p className="code mt-1 rounded-md bg-surface-raised px-2.5 py-1.5 break-all text-ink-muted shadow-[inset_0_0_0_1px_var(--line)]">
              {error.message}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={reset}>
            <IconRefresh />
            Try again
          </Button>
          <Button variant="outline" render={<Link to="/voxlane" />}>
            <IconHome2 />
            Back to dashboard
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Fired when the URL doesn't match any defined route. */
export function RouteNotFound() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-ground px-6">
      <div className="flex w-full max-w-sm flex-col items-start gap-5">
        <span className="eyebrow text-signal-ink!">404 · Not found</span>
        <div className="flex flex-col gap-2">
          <h1 className="text-display">Nothing here</h1>
          <p className="text-sm text-ink-muted">
            This address doesn&apos;t match any page. It may have moved, or
            the link was mistyped.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button render={<Link to="/voxlane" />}>
            <IconHome2 />
            Dashboard
          </Button>
          <Button
            variant="ghost"
            onClick={() => window.history.back()}
            aria-label="Go to previous page"
          >
            <IconArrowLeft />
            Go back
          </Button>
        </div>
      </div>
    </div>
  )
}
